import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAppState } from "../context/AppState";
import { useLocale } from "../context/Locale";
import { api, authenticatedApi } from "../lib/api";
import { cartSubtotal, deliveryQuote, distanceKm, money } from "../lib/orderMath";

function checkoutDraft() {
  try {
    return JSON.parse(sessionStorage.getItem("deliveryCheckoutDraft") || "{}");
  } catch {
    return {};
  }
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { address, cart, clearCart, user } = useAppState();
  const { t } = useLocale();
  const [restaurant, setRestaurant] = useState(null);
  const [stage, setStage] = useState("gateways");
  const [paymentSubtype, setPaymentSubtype] = useState("");
  const [cashChange, setCashChange] = useState("");
  const [paymentGateways, setPaymentGateways] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const draft = useMemo(checkoutDraft, []);

  useEffect(() => {
    if (!cart.length) return;
    api(`/get-restaurant-info/${cart[0].restaurantSlug}`, {
      method: "POST",
      body: "{}",
    }).then(setRestaurant).catch((requestError) => setError(requestError.message));
  }, [cart]);

  useEffect(() => {
    if (!user?.auth_token || !restaurant?.id) return;
    authenticatedApi("/get-payment-gateways", user.auth_token, {
      method: "POST",
      body: JSON.stringify({ restaurant_id: restaurant.id }),
    })
      .then((gateways) => setPaymentGateways(Array.isArray(gateways) ? gateways : []))
      .catch(() => {});
  }, [restaurant, user]);

  const subtotal = useMemo(() => cartSubtotal(cart), [cart]);
  const isYapePromoOrder = useMemo(
    () => cart.some((line) => Boolean(line.isYapePromo)),
    [cart],
  );
  const quote = useMemo(() => deliveryQuote(restaurant, address), [address, restaurant]);
  const isPickup = draft.fulfillmentType === "pickup" && Number(restaurant?.delivery_type) === 3;
  const deliveryCharge = isPickup ? 0 : quote.charge;
  const restaurantCharge = Number(restaurant?.restaurant_charges || 0);
  const discount = draft.coupon
    ? draft.coupon.discount_type === "PERCENTAGE"
      ? Math.min(
          subtotal * (Number(draft.coupon.discount || 0) / 100),
          Number(draft.coupon.max_discount || Number.MAX_SAFE_INTEGER),
        )
      : Math.min(subtotal, Number(draft.coupon.discount || 0))
    : 0;
  const total = Math.max(0, subtotal + deliveryCharge + restaurantCharge - discount);
  const restaurantAcceptsOrders = Number(restaurant?.is_active) === 1
    && Number(restaurant?.is_accepted ?? 1) === 1
    && Number(restaurant?.commission_blocked || 0) !== 1;

  async function placeOrder() {
    if (!user?.auth_token) {
      navigate("/account?next=/checkout");
      return;
    }
    if (!restaurant || (!isPickup && !address)) {
      navigate("/cart");
      return;
    }
    if (!restaurantAcceptsOrders) {
      setError("Este negocio está cerrado en este momento y no acepta pedidos. Inténtalo dentro de su horario de atención.");
      return;
    }
    if (!paymentSubtype) return;
    if (isYapePromoOrder && paymentSubtype !== "YAPE") {
      setError("Las Promos Yape solo pueden pagarse con Yape.");
      return;
    }

    const addressVerification = draft.addressVerification;
    const verificationIsCurrent = isPickup || (
      addressVerification
      && Date.now() - Number(addressVerification.verifiedAt || 0) <= 15 * 60 * 1000
      && Math.abs(Number(addressVerification.latitude) - Number(address.lat)) < 0.000001
      && Math.abs(Number(addressVerification.longitude) - Number(address.lng)) < 0.000001
    );
    if (!verificationIsCurrent) {
      setError(t("addressVerificationExpired"));
      window.setTimeout(() => navigate("/cart"), 900);
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      const customerLocation = isPickup
        ? { lat: Number(restaurant.latitude), lng: Number(restaurant.longitude) }
        : { lat: Number(address.lat), lng: Number(address.lng) };
      const distance = isPickup
        ? 0
        : distanceKm(
            { lat: Number(restaurant.latitude), lng: Number(restaurant.longitude) },
            customerLocation,
          );
      const response = await authenticatedApi("/place-order", user.auth_token, {
        method: "POST",
        body: JSON.stringify({
          order: cart.map((line) => ({
            id: line.itemId,
            name: line.name,
            price: line.price,
            quantity: line.quantity,
            restaurant_id: line.restaurantId,
            selectedaddons: line.selectedAddons || [],
          })),
          user: {
            data: {
              ...user,
              default_address: {
                house: user?.default_address?.house || "",
                address: isPickup ? (restaurant.address || restaurant.name) : address.address,
                latitude: customerLocation.lat,
                longitude: customerLocation.lng,
              },
            },
          },
          location: customerLocation,
          dis: distance,
          delivery_type: isPickup ? 2 : 1,
          method: "COD",
          cash_payment_method: paymentSubtype,
          cash_change_amount: paymentSubtype === "CASH" && cashChange ? Number(cashChange) : null,
          pending_payment: false,
          payment_token: null,
          partial_wallet: false,
          coupon: draft.coupon || null,
          tipAmount: 0,
          order_comment: draft.comment || "",
          use_delivery_credits: false,
        }),
      });

      if (!response?.success) throw new Error(response?.message || "No se pudo crear el pedido.");
      clearCart();
      sessionStorage.removeItem("deliveryCheckoutDraft");
      navigate(`/orders?placed=${encodeURIComponent(response.data?.unique_order_id || "")}`);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!cart.length) {
    return (
      <main className="legacy-flow-page">
        <header className="flow-header"><Link to="/food">‹</Link><strong>Pago</strong></header>
        <section className="empty-cart checkout-empty">
          <span>✓</span>
          <h2>No hay un pedido por confirmar</h2>
          <Link to="/food">Ir a los negocios</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="legacy-flow-page payment-flow">
      <header className="flow-header flow-header--payment">
        <Link to={stage === "gateways" ? "/cart" : "#"} onClick={(event) => {
          if (stage !== "gateways") {
            event.preventDefault();
            setStage("gateways");
            setPaymentSubtype("");
          }
        }}>‹</Link>
        <span>{t("totalToPay").toUpperCase()}</span>
        <b>{money(total)}</b>
      </header>

      {stage === "gateways" ? (
        <section className="payment-gateways">
          <h1>{t("paymentMethodForLocation")}</h1>
          {isYapePromoOrder && (
            <div className="yape-promo-payment-note">
              <strong>{t("yapeExclusivePayment")}</strong>
              <span>{t("yapePromoOrderNotice")}</span>
            </div>
          )}
          <div className={`checkout-fulfillment-summary ${isPickup ? "is-pickup" : ""}`.trim()}>
            <span aria-hidden="true">{isPickup ? "🏪" : "🛵"}</span>
            <div>
              <b>{isPickup ? t("pickupAtStore") : t("deliveryOption")}</b>
              <small>
                {isPickup
                  ? `${restaurant?.name || ""} · ${t("noDeliveryFee")}`
                  : `${address?.address || t("selectAnAddress")} · ${money(deliveryCharge)}`}
              </small>
            </div>
            <Link to="/cart">{t("change")}</Link>
          </div>
          <button
            type="button"
            className="payment-gateway-card payment-gateway-card--delivery"
            onClick={() => {
              setStage("cod");
              if (isYapePromoOrder) setPaymentSubtype("YAPE");
            }}
            aria-label={t("choosePaymentOnDelivery")}
          >
            <span className="payment-gateway-card__icon" aria-hidden="true">{isYapePromoOrder ? "Y" : "💵"}</span>
            <div className="payment-gateway-card__copy">
              <small>{isYapePromoOrder ? t("exclusivePaymentMethod") : t("payOnDeliveryLabel")}</small>
              <strong>{isYapePromoOrder ? t("payWithYape") : t("payOnDeliveryTitle")}</strong>
              <span>{isYapePromoOrder ? t("keepPromotionalPrice") : t("payOnDeliveryHelp")}</span>
            </div>
            <span className="payment-gateway-card__action">
              {t("choosePayment")} <i aria-hidden="true">›</i>
            </span>
          </button>
          {paymentGateways
            .filter((gateway) => String(gateway.name).toUpperCase() !== "COD")
            .map((gateway) => (
              <button type="button" className="payment-gateway-card" key={gateway.id} disabled>
                <div><strong>{gateway.name}</strong><span>{t("integrationPending")}</span></div>
                <span className="payment-gateway-card__icon">🌐</span>
              </button>
            ))}
        </section>
      ) : (
        <section className="cod-payment">
          <h1>{t("choosePaymentOnDelivery")}</h1>
          {restaurant && !restaurantAcceptsOrders && (
            <div className="checkout-store-closed" role="alert">
              <strong>{t("storeClosed")}</strong>
              <span>{t("checkoutStoreClosedHelp")}</span>
            </div>
          )}
          <div className="cod-payment__question">
            <strong>{isPickup ? t("howPayPickup") : t("howPayDelivery")}</strong>
            <span>{t("selectOptionToContinue")}</span>
          </div>
          <div className="cod-payment__methods">
            {[
              ["CASH", "S/", t("cash"), t("cashChangeHelp")],
              ["YAPE", "Y", "Yape", t("coordinatedTransfer")],
              ["PLIN", "P", "Plin", t("coordinatedTransfer")],
            ].filter(([value]) => !isYapePromoOrder || value === "YAPE").map(([value, icon, title, help]) => (
              <button
                type="button"
                key={value}
                className={paymentSubtype === value ? "is-selected" : ""}
                aria-pressed={paymentSubtype === value}
                onClick={() => setPaymentSubtype(value)}
              >
                <i>{icon}</i>
                <div><strong>{title}</strong><span>{help}</span></div>
              </button>
            ))}
          </div>

          {paymentSubtype === "CASH" && (
            <input
              className="cash-change-input"
              inputMode="decimal"
              value={cashChange}
              onChange={(event) => setCashChange(event.target.value.replace(/[^\d.]/g, ""))}
              placeholder={t("cashAmountPlaceholder")}
            />
          )}

          {paymentSubtype && (
            <>
              <button
                className="cod-payment__confirm"
                type="button"
                disabled={submitting || !restaurantAcceptsOrders}
                onClick={placeOrder}
              >
                {submitting
                  ? t("processing")
                  : paymentSubtype === "CASH"
                    ? t("confirmCashOrder")
                    : t("confirmOrderWith").replace("{method}", paymentSubtype === "YAPE" ? "Yape" : "Plin")}
              </button>
              <p className="cod-payment__help">
                {paymentSubtype === "CASH"
                  ? t("cashChangeInstructions")
                  : t(isPickup ? "transferPickupInstructions" : "transferDeliveryInstructions")
                    .replace("{method}", paymentSubtype === "YAPE" ? "Yape" : "Plin")}
              </p>
            </>
          )}
          {!paymentSubtype && (
            <button className="cod-payment__confirm" type="button" disabled>
              {t("selectPaymentMethod")}
            </button>
          )}
          {error && <div className="checkout-error">{error}</div>}
        </section>
      )}
    </main>
  );
}
